/**
 * AirportInfoPanel — Comprehensive airport information screen with
 * tabbed interface for overview, runways, communications, procedures,
 * charts, and weather. Data sources: NASR database, CIFP database,
 * chart server, and AWC weather.
 */
import { useState, useEffect } from 'react';
import type { ResolvedWaypoint, ParsedMetar, ParsedTaf } from '@efb/shared-core';
import { useWeather } from '../hooks/useWeather';
import { useAirportDetails } from '../hooks/useAirportDetails';
import { RunwayDiagram } from '../components/RunwayDiagram';
import { analyzeRunwayEndWind } from '../services/runway-wind-analysis';
import type {
  AirportDetail,
  Runway,
  RunwayEnd,
  CommunicationFrequency,
  Procedure,
  ChartMetadata,
} from '../types/airport';

interface Props {
  airport: ResolvedWaypoint | null;
  onClose: () => void;
  onAddToRoute?: (airport: ResolvedWaypoint) => void;
}

type TabId = 'overview' | 'runways' | 'comms' | 'procedures' | 'charts' | 'weather';

const TABS: { id: TabId; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'runways', label: 'Runways' },
  { id: 'comms', label: 'Comms' },
  { id: 'procedures', label: 'Procedures' },
  { id: 'charts', label: 'Charts' },
  { id: 'weather', label: 'Weather' },
];

const CATEGORY_COLORS: Record<string, string> = {
  VFR: '#22C55E', MVFR: '#3B82F6', IFR: '#EF4444', LIFR: '#D946EF',
};

export function AirportInfoPanel({ airport, onClose, onAddToRoute }: Props) {
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const { data: wxData, loading: wxLoading, fetchWeather } = useWeather();
  const {
    detail, procedures, charts,
    loading, proceduresLoading, chartsLoading, error,
    fetchAirport,
  } = useAirportDetails();

  useEffect(() => {
    if (airport) {
      void fetchAirport(airport.identifier);
      void fetchWeather(airport.identifier);
    }
  }, [airport, fetchAirport, fetchWeather]);

  if (!airport) return null;

  const metar: ParsedMetar | null = wxData?.metars?.[0] ?? null;
  const category = metar ? metar.flightCategory : null;

  return (
    <div
      className="panel airport-info-panel"
      role="region"
      aria-label={`Airport ${airport.identifier}`}
    >
      <div className="airport-header">
        <h2>
          {detail?.icao ?? airport.identifier}
          {category && (
            <span
              className="flight-cat"
              style={{ backgroundColor: CATEGORY_COLORS[category] }}
            >
              {category}
            </span>
          )}
        </h2>
        <button className="fp-btn" onClick={onClose} aria-label="Close">✕</button>
      </div>
      <p className="airport-name">{detail?.name ?? airport.name}</p>
      {detail && (
        <p className="airport-location">
          {[detail.city, detail.state, detail.country].filter(Boolean).join(', ')}
        </p>
      )}

      {loading && <p className="loading-text">Loading airport data…</p>}
      {error && <p className="error-text">{error}</p>}

      {/* Tab navigation */}
      <div className="airport-tabs" role="tablist">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={activeTab === tab.id}
            className={`airport-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="airport-tab-content" role="tabpanel">
        {activeTab === 'overview' && (
          <OverviewTab detail={detail} airport={airport} />
        )}
        {activeTab === 'runways' && (
          <RunwaysTab runways={detail?.runways ?? []} metar={metar} />
        )}
        {activeTab === 'comms' && (
          <CommsTab communications={detail?.communications ?? []} />
        )}
        {activeTab === 'procedures' && (
          <ProceduresTab
            procedures={procedures}
            loading={proceduresLoading}
          />
        )}
        {activeTab === 'charts' && (
          <ChartsTab charts={charts} loading={chartsLoading} />
        )}
        {activeTab === 'weather' && (
          <WeatherTab
            metar={metar}
            taf={wxData?.tafs?.[0] ?? null}
            loading={wxLoading}
          />
        )}
      </div>

      {onAddToRoute && (
        <button
          className="fp-btn fp-btn-primary add-to-route-btn"
          onClick={() => onAddToRoute(airport)}
        >
          Add to Route
        </button>
      )}
    </div>
  );
}

// ─── Tab components ──────────────────────────────────────────────────

function OverviewTab({
  detail,
  airport,
}: {
  detail: AirportDetail | null;
  airport: ResolvedWaypoint;
}) {
  const lat = detail?.latitude ?? airport.latitude;
  const lon = detail?.longitude ?? airport.longitude;
  const elev = detail?.elevation ?? airport.elevation ?? 0;

  return (
    <div className="overview-tab">
      <div className="overview-grid">
        <DataRow label="ICAO" value={detail?.icao ?? airport.identifier} />
        {detail?.faaId && detail.faaId !== detail.icao && (
          <DataRow label="FAA ID" value={detail.faaId} />
        )}
        <DataRow
          label="Position"
          value={`${lat.toFixed(5)}°, ${lon.toFixed(5)}°`}
        />
        <DataRow label="Elevation" value={`${elev} ft MSL`} />
        {detail?.magneticVariation !== undefined && detail.magneticVariation !== 0 && (
          <DataRow
            label="Mag Var"
            value={`${Math.abs(detail.magneticVariation).toFixed(1)}° ${
              detail.magneticVariation < 0 ? 'W' : 'E'
            }`}
          />
        )}
        {detail?.patternAltitude && (
          <DataRow label="Pattern Alt" value={`${detail.patternAltitude} ft AGL`} />
        )}
        {detail?.airspaceClass && (
          <DataRow label="Airspace" value={`Class ${detail.airspaceClass}`} />
        )}
        {detail?.operating.towerType && (
          <DataRow
            label="Tower"
            value={detail.operating.towerType === 'ATCT' ? 'Yes (ATCT)' : 'No'}
          />
        )}
        {detail?.operating.use && (
          <DataRow
            label="Use"
            value={detail.operating.use === 'PU' ? 'Public' : 'Private'}
          />
        )}
        {detail?.operating.hours && (
          <DataRow label="Hours" value={detail.operating.hours} />
        )}
        {detail?.notamFacility && (
          <DataRow label="NOTAM" value={detail.notamFacility} />
        )}
      </div>

      {detail?.services.fuel && detail.services.fuel.length > 0 && (
        <section className="overview-section">
          <h3>Fuel</h3>
          <div className="fuel-badges">
            {detail.services.fuel.map((f) => (
              <span key={f} className="fuel-badge">{f}</span>
            ))}
          </div>
        </section>
      )}

      {detail && (detail.services.oxygen || detail.services.repair || detail.services.storage) && (
        <section className="overview-section">
          <h3>Services</h3>
          <ul className="service-list">
            {detail.services.oxygen && <li>Oxygen available</li>}
            {detail.services.repair && <li>Repair services</li>}
            {detail.services.storage && <li>Hangar/storage</li>}
            {detail.services.landingFee && <li>Landing fees</li>}
          </ul>
        </section>
      )}

      {detail?.remarks && (
        <section className="overview-section">
          <h3>Remarks</h3>
          <p className="airport-remarks">{detail.remarks}</p>
        </section>
      )}

      {detail?.nasrCycle && (
        <section className="overview-section data-source">
          <small>
            NASR cycle effective {detail.nasrCycle.effectiveDate}
            {detail.nasrCycle.expirationDate && ` – ${detail.nasrCycle.expirationDate}`}
          </small>
        </section>
      )}
    </div>
  );
}

function RunwaysTab({ runways, metar }: { runways: Runway[]; metar: ParsedMetar | null }) {
  if (runways.length === 0) {
    return <p className="empty-state">No runway data available.</p>;
  }

  return (
    <div className="runways-tab">
      {metar && (
        <div className="runway-wind-summary">
          Wind {metar.windDirection}° at {metar.windSpeed} kt
          {metar.windGust ? ` gusting ${metar.windGust} kt` : ''}
        </div>
      )}
      <div className="runway-diagram-container">
        <RunwayDiagram runways={runways} width={320} height={320} />
      </div>
      <div className="runway-list">
        {runways.map((rwy) => (
          <RunwayCard key={rwy.identifier} runway={rwy} metar={metar} />
        ))}
      </div>
    </div>
  );
}

function RunwayCard({ runway, metar }: { runway: Runway; metar: ParsedMetar | null }) {
  return (
    <div className="runway-card">
      <div className="runway-card-header">
        <h4>{runway.identifier}</h4>
        <span className="runway-dims">
          {runway.length.toLocaleString()} × {runway.width} ft
        </span>
      </div>
      <div className="runway-card-meta">
        <span className="runway-meta-pill">{runway.surface}</span>
        {runway.edgeLighting && (
          <span className="runway-meta-pill">LGT: {runway.edgeLighting}</span>
        )}
        {runway.centerlineLighting && (
          <span className="runway-meta-pill">CL</span>
        )}
        {runway.closed && <span className="runway-meta-pill closed">CLOSED</span>}
      </div>
      <div className="runway-ends">
        <RunwayEndCard end={runway.baseEnd} metar={metar} />
        <RunwayEndCard end={runway.reciprocalEnd} metar={metar} />
      </div>
    </div>
  );
}

function RunwayEndCard({ end, metar }: { end: RunwayEnd; metar: ParsedMetar | null }) {
  const wind = metar
    ? analyzeRunwayEndWind(end.designator, end.magneticHeading, metar.windDirection, metar.windSpeed)
    : null;

  return (
    <div className="runway-end">
      <div className="runway-end-header">
        <strong>{end.designator}</strong>
        {end.magneticHeading !== undefined && (
          <span className="runway-heading">{Math.round(end.magneticHeading)}°</span>
        )}
      </div>
      <dl className="runway-end-details">
        {wind && (
          <>
            <dt>Wind</dt>
            <dd>
              <span className={`runway-wind-class runway-wind-${wind.classification}`}>
                {wind.classification}
              </span>
            </dd>
            <dt>Head</dt><dd>{wind.headwindKts.toFixed(1)} kt</dd>
            <dt>Cross</dt><dd>{wind.crosswindKts.toFixed(1)} kt</dd>
            <dt>Tail</dt><dd>{wind.tailwindKts.toFixed(1)} kt</dd>
          </>
        )}
        {end.elevation !== undefined && end.elevation !== 0 && (
          <><dt>Elev</dt><dd>{end.elevation} ft</dd></>
        )}
        {end.displacedThreshold !== undefined && end.displacedThreshold > 0 && (
          <><dt>Displaced</dt><dd>{end.displacedThreshold} ft</dd></>
        )}
        {end.thresholdCrossingHeight !== undefined && (
          <><dt>TCH</dt><dd>{end.thresholdCrossingHeight} ft</dd></>
        )}
        {end.vgsi && (
          <><dt>VGSI</dt><dd>{end.vgsi}</dd></>
        )}
        {end.ilsFrequency !== undefined && (
          <><dt>ILS</dt><dd>{end.ilsFrequency.toFixed(2)} MHz{end.ilsIdentifier ? ` (${end.ilsIdentifier})` : ''}</dd></>
        )}
        {end.approachLighting && (
          <><dt>ALS</dt><dd>{end.approachLighting}</dd></>
        )}
        {end.reil && (
          <><dt>REIL</dt><dd>Yes</dd></>
        )}
      </dl>
    </div>
  );
}

function CommsTab({
  communications,
}: {
  communications: CommunicationFrequency[];
}) {
  if (communications.length === 0) {
    return <p className="empty-state">No communication frequencies available.</p>;
  }

  // Group by type (ATIS, TWR, GND, etc.)
  const grouped = groupBy(communications, (c) => c.type);
  const typeOrder = ['ATIS', 'CTAF', 'TWR', 'GND', 'CLR', 'APCH', 'DEP', 'UNICOM', 'MULTICOM', 'CENTER'];
  const sortedTypes = Object.keys(grouped).sort((a, b) => {
    const aIdx = typeOrder.indexOf(a);
    const bIdx = typeOrder.indexOf(b);
    if (aIdx === -1 && bIdx === -1) return a.localeCompare(b);
    if (aIdx === -1) return 1;
    if (bIdx === -1) return -1;
    return aIdx - bIdx;
  });

  return (
    <div className="comms-tab">
      <table className="comms-table">
        <thead>
          <tr>
            <th>Service</th>
            <th>Frequency</th>
            <th>Notes</th>
          </tr>
        </thead>
        <tbody>
          {sortedTypes.map((type) => (
            grouped[type].map((freq, idx) => (
              <tr key={`${type}-${idx}`}>
                <td className="comms-type">
                  {idx === 0 ? <strong>{type}</strong> : ''}
                </td>
                <td className="comms-freq">{freq.frequency.toFixed(3)}</td>
                <td className="comms-name">
                  {freq.name ?? freq.sectorFacility ?? ''}
                </td>
              </tr>
            ))
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProceduresTab({
  procedures,
  loading,
}: {
  procedures: Procedure[];
  loading: boolean;
}) {
  if (loading) return <p className="loading-text">Loading procedures…</p>;
  if (procedures.length === 0) {
    return <p className="empty-state">No procedures available.</p>;
  }

  const approaches = procedures.filter((p) => p.type === 'APPROACH');
  const sids = procedures.filter((p) => p.type === 'SID');
  const stars = procedures.filter((p) => p.type === 'STAR');

  return (
    <div className="procedures-tab">
      {approaches.length > 0 && (
        <section>
          <h3>Instrument Approaches ({approaches.length})</h3>
          <ul className="procedure-list">
            {approaches.map((p) => (
              <li key={p.identifier} className="procedure-item">
                <span className="procedure-ident">{p.identifier}</span>
                {p.approachType && (
                  <span className="procedure-type-badge">{p.approachType}</span>
                )}
                {p.runway && <span className="procedure-rwy">RWY {p.runway}</span>}
                {p.hasChart && <span className="chart-available">📄</span>}
              </li>
            ))}
          </ul>
        </section>
      )}
      {sids.length > 0 && (
        <section>
          <h3>Departure Procedures (SIDs) ({sids.length})</h3>
          <ul className="procedure-list">
            {sids.map((p) => (
              <li key={p.identifier} className="procedure-item">
                <span className="procedure-ident">{p.identifier}</span>
                {p.transitions && p.transitions.length > 0 && (
                  <span className="transition-count">
                    {p.transitions.length} transitions
                  </span>
                )}
                {p.hasChart && <span className="chart-available">📄</span>}
              </li>
            ))}
          </ul>
        </section>
      )}
      {stars.length > 0 && (
        <section>
          <h3>Arrival Procedures (STARs) ({stars.length})</h3>
          <ul className="procedure-list">
            {stars.map((p) => (
              <li key={p.identifier} className="procedure-item">
                <span className="procedure-ident">{p.identifier}</span>
                {p.transitions && p.transitions.length > 0 && (
                  <span className="transition-count">
                    {p.transitions.length} transitions
                  </span>
                )}
                {p.hasChart && <span className="chart-available">📄</span>}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function ChartsTab({
  charts,
  loading,
}: {
  charts: ChartMetadata[];
  loading: boolean;
}) {
  if (loading) return <p className="loading-text">Loading charts…</p>;
  if (charts.length === 0) {
    return <p className="empty-state">No charts available for this airport.</p>;
  }

  const grouped = groupBy(charts, (c) => c.type);
  const typeLabels: Record<ChartMetadata['type'], string> = {
    APD: 'Airport Diagram',
    IAP: 'Approach Plates',
    DP:  'Departure Procedures',
    STAR: 'Arrival Procedures',
    MIN: 'Takeoff Minimums',
  };

  return (
    <div className="charts-tab">
      {(Object.keys(typeLabels) as ChartMetadata['type'][]).map((type) => {
        const items = grouped[type];
        if (!items || items.length === 0) return null;
        return (
          <section key={type} className="chart-section">
            <h3>{typeLabels[type]}</h3>
            <ul className="chart-list">
              {items.map((chart) => (
                <li key={chart.id} className="chart-item">
                  {chart.pdfUrl ? (
                    <a
                      href={chart.pdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="chart-link"
                    >
                      {chart.name}
                    </a>
                  ) : (
                    <span>{chart.name}</span>
                  )}
                  {chart.georeferenced && (
                    <span className="chart-georef">GEO</span>
                  )}
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

function WeatherTab({
  metar,
  taf,
  loading,
}: {
  metar: ParsedMetar | null;
  taf: ParsedTaf | null;
  loading: boolean;
}) {
  if (loading) return <p className="loading-text">Loading weather…</p>;
  if (!metar && !taf) {
    return <p className="empty-state">No weather data available.</p>;
  }

  return (
    <div className="weather-tab">
      {metar && (
        <fieldset className="weather-section">
          <legend>METAR</legend>
          <pre className="raw-wx">{metar.rawText}</pre>
          <div className="wx-details">
            <span>Wind: {metar.windDirection}° @ {metar.windSpeed}kt{metar.windGust ? ` G${metar.windGust}` : ''}</span>
            <span>Vis: {metar.visibility} SM</span>
            <span>Ceil: {metar.ceiling ?? 'CLR'}</span>
            <span>Temp: {metar.temperature}°C / Dew: {metar.dewpoint}°C</span>
            <span>Alt: {metar.altimeter.toFixed(2)}</span>
          </div>
        </fieldset>
      )}
      {taf && (
        <fieldset className="weather-section">
          <legend>TAF</legend>
          <pre className="raw-wx">{taf.rawText}</pre>
        </fieldset>
      )}
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────

function DataRow({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="data-row">
      <span className="data-label">{label}</span>
      <span className="data-value">{value}</span>
    </div>
  );
}

function groupBy<T, K extends string>(
  items: T[],
  keyFn: (item: T) => K,
): Record<K, T[]> {
  const result = {} as Record<K, T[]>;
  for (const item of items) {
    const key = keyFn(item);
    if (!result[key]) result[key] = [];
    result[key].push(item);
  }
  return result;
}
